import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, TrendingDown, Loader2, Building2, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getCondominiosMetricasMensal } from "@/lib/api";
import { fmtInt } from "@/lib/format";

const monthLabel = (mp) => {
  if (!mp) return "";
  try {
    return new Date(`${mp}-01T00:00:00`).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
  } catch { return mp; }
};

// Alerta pros consultores: condomínios que não bateram a meta mensal
// nos 2 últimos meses completos consecutivos — sinal de perda de tração.
export default function AlertaRitmoCondoCard({ onNavigate }) {
  const [rows, setRows] = useState([]);
  const [meses, setMeses] = useState({ mes_1: "", mes_2: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getCondominiosMetricasMensal();
      setRows(Array.isArray(r?.sem_ritmo) ? r.sem_ritmo : []);
      setMeses({ mes_1: r?.mes_1 || "", mes_2: r?.mes_2 || "" });
    } catch (e) {
      console.error("Erro alerta ritmo:", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Verificando ritmo dos condomínios…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-center gap-3"
        data-testid="alerta-ritmo-empty"
      >
        <div className="w-10 h-10 rounded-lg bg-emerald-500/15 grid place-items-center shrink-0">
          <TrendingDown className="w-5 h-5 text-emerald-500 rotate-180" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-sm">Ritmo em dia</h3>
          <p className="text-xs text-muted-foreground">Nenhum condomínio ficou 2 meses seguidos abaixo da meta mensal.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border-2 border-red-500/40 bg-gradient-to-br from-red-500/10 via-red-500/5 to-transparent p-5 space-y-3"
      data-testid="alerta-ritmo-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/15 grid place-items-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-semibold text-lg">Prédios perdendo ritmo</h3>
              <Badge className="bg-red-500 text-white border-0 tabular-nums" data-testid="badge-ritmo-count">
                {rows.length}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              Condomínios que ficaram <strong className="text-red-500">abaixo da meta mensal</strong> em <strong>{monthLabel(meses.mes_2)}</strong> e <strong>{monthLabel(meses.mes_1)}</strong>. Priorize contato com o consultor.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onNavigate?.()}
            data-testid={`ritmo-row-${r.id}`}
            className="w-full rounded-lg border border-border bg-card/80 p-3 flex items-center gap-3 text-left hover:bg-accent transition-all"
          >
            <Building2 className="w-4 h-4 text-red-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm truncate">{r.condominio}</span>
                {r.consultor ? (
                  <Badge variant="outline" className="text-[10px] text-primary border-primary/40">
                    <User className="w-3 h-3 mr-1" /> {r.consultor}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/40">
                    Sem consultor
                  </Badge>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums flex flex-wrap gap-x-3">
                <span>{monthLabel(meses.mes_2)}: <strong className="text-red-500">{fmtInt(r.realizado_mes_2)}</strong>/{fmtInt(r.meta_mensal)}</span>
                <span>{monthLabel(meses.mes_1)}: <strong className="text-red-500">{fmtInt(r.realizado_mes_1)}</strong>/{fmtInt(r.meta_mensal)}</span>
                <span className="text-muted-foreground">este mês: {fmtInt(r.clientes_mes_atual)}</span>
              </div>
            </div>
            <Button variant="outline" size="sm" className="shrink-0 text-red-500 border-red-500/40 hover:bg-red-500/10">
              Abrir
            </Button>
          </button>
        ))}
      </div>
    </div>
  );
}
